/** @type {string} V2互換方式のテスト環境URL */
const GMO_PG_ENDPOINT_DEFAULT = 'https://stg.static.mul-pay.jp/payment/js/mp-token.js';
const TIMEOUT_MS = 30000;
/** @type {Promise<void>|null} */
let libraryPromise = null;
let tokenRequestPending = false;
let callbackSequence = 0;

const GMO = {
  /**
   * V2ライブラリを一度だけ読み込み、利用可能になるまで待機する。
   * @returns {Promise<void>}
   */
  importGmoPGLibrary() {
    if (libraryPromise) {
      return libraryPromise;
    }

    libraryPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const timer = setTimeout(() => finish(new Error('GMOライブラリの読み込みがタイムアウトしました')), TIMEOUT_MS);
      /** 読み込み結果を確定してイベントとタイマーを解放する。 */
      function finish(error) {
        clearTimeout(timer);
        script.onload = null;
        script.onerror = null;
        if (error) {
          script.remove();
          reject(error);
        } else {
          resolve();
        }
      }
      script.onload = () => finish(window.Multipayment ? null : new Error('GMOライブラリを利用できません'));
      script.onerror = () => finish(new Error('GMOライブラリを読み込めません'));
      script.src = process.env.VUE_APP_GMO_PG_ENDPOINT || GMO_PG_ENDPOINT_DEFAULT;
      document.head.appendChild(script);
    }).catch(error => {
      libraryPromise = null;
      throw error;
    });
    return libraryPromise;
  },

  /**
   * 指定数のトークンを取得し、既存の応答形式で返す。
   * @param {Object} cardObject カード入力情報
   * @param {number} tokenCount 発行数
   * @returns {Promise<Object>}
   */
  async getToken(cardObject, tokenCount = 2) {
    if (!Number.isInteger(tokenCount) || tokenCount < 1 || tokenCount > 10) {
      throw new Error('トークン発行数は1〜10で指定してください');
    }
    if (tokenRequestPending) {
      throw new Error('トークン取得中です');
    }
    tokenRequestPending = true;
    try {
      await GMO.importGmoPGLibrary();
      return await new Promise((resolve, reject) => {
        // 遅延した応答が次の要求に混入しないよう要求ごとに名前を分ける
        const callbackName = `gmoTokenCallback_${++callbackSequence}`;
        let settled = false;
        const timer = setTimeout(() => finish(new Error('トークン取得がタイムアウトしました')), TIMEOUT_MS);
        /** 応答を一度だけ確定し、公開コールバックを解放する。 */
        function finish(error, response) {
          if (settled) {
            return;
          }
          settled = true;
          clearTimeout(timer);
          delete window[callbackName];
          if (error) {
            reject(error);
          } else {
            resolve(response);
          }
        }
        /** 成功コードと発行数を検証する。 */
        const callback = function gmoTokenCallback(response) {
          const tokens = response && response.tokenObject && response.tokenObject.token;
          if (!response || response.resultCode !== '000') {
            finish(new Error('カード情報をトークン化できません'));
          } else if (!Array.isArray(tokens) || tokens.length !== tokenCount ||
            tokens.some(token => typeof token !== 'string' || token.trim() === '')) {
            finish(new Error('トークンの応答が不正です'));
          } else {
            finish(null, response);
          }
        };
        // SDKが参照する関数名をビルド時の圧縮から独立させる
        Object.defineProperty(callback, 'name', {value: callbackName});
        window[callbackName] = callback;
        try {
          // 既存の環境変数はV2 APIキーではなくショップIDを保持する
          window.Multipayment.init(process.env.VUE_APP_GMO_PG_API_KEY);
          Promise.resolve(window.Multipayment.getToken({
            ...cardObject,
            tokennumber: String(tokenCount),
          }, callback)).catch(() => finish(new Error('トークン取得に失敗しました')));
        } catch (error) {
          finish(new Error('トークン取得に失敗しました'));
        }
      });
    } finally {
      tokenRequestPending = false;
    }
  },
};

export default GMO;
